/**
 * Unified sidebar filter hook — single source of truth for all item visibility.
 *
 * Evaluates three categories of rules declared in sidebarConfig.ts:
 * 1. Licenses (`visibility.requiresLicenses`) — location-level and cage-level
 * 2. Feature flags (`visibility.requiredFeatureFlags`) — PostHog-based
 * 3. Business rules (`visibility.*`) — smolt, winch, bioscope version (v1 / v2 / non-v1),
 *    lice variant, behavior module, client-admin permission
 *
 * OptoScale admins bypass license checks entirely.
 * sidebarConfig.ts is the source of truth — this hook never checks item IDs.
 */

import { useCallback, useMemo } from 'react';
import { useActiveFeatureFlags } from 'posthog-js/react';

import { usePermissions } from '@/contexts/auth';
import { useGetCageContext } from '@/domain/cages';
import { type Licenses, type LicenseValidity, useGetLocationLicenses } from '@/domain/licenses';
import { isHeldOpen, useHeldOpenFlags } from '@/hooks/heldOpenFlags';
import { isBioscopeV2, isColorBioscopeVersion } from '@/utils/bioscopeVersionHelpers';
import { isChileanLiceType, isSmoltProductionTank } from '@/utils/locationTypeHelpers';

import type { SidebarLevel, VisibilityRules } from './sidebarConfig';
import type { SidebarNavItem } from './useSidebarNavigation';

// ---------------------------------------------------------------------------
// Visibility context — runtime values the evaluator needs
// ---------------------------------------------------------------------------

interface FilterContext {
    isSmolt: boolean;
    winchExists: boolean;
    isBioscopeV1: boolean;
    isBioscopeV2: boolean;
    isColorBioscope: boolean;
    isChileanLice: boolean;
    isClientAdmin: boolean;
    canAdministerCurrentLocation: boolean;
    isOptoScaleAdmin: boolean;
    canViewAuditLog: boolean;
    isFeatureEnabled: (flag: string) => boolean;
    activeLicenseKeys: Set<string>;
}

const DEFAULT_CAGE_VALUES: Pick<
    FilterContext,
    | 'isSmolt'
    | 'winchExists'
    | 'isBioscopeV1'
    | 'isBioscopeV2'
    | 'isColorBioscope'
    | 'isChileanLice'
> = {
    isSmolt: false,
    winchExists: false,
    isBioscopeV1: false,
    isBioscopeV2: false,
    isColorBioscope: false,
    isChileanLice: false,
};

// ---------------------------------------------------------------------------
// License helpers
// ---------------------------------------------------------------------------

function getActiveLicenseKeys(licenses: Licenses): Set<string> {
    const keys = new Set<string>();
    for (const [key, value] of Object.entries(licenses) as [string, LicenseValidity][]) {
        if (value?.hasValidLicense) {
            keys.add(key);
        }
    }
    return keys;
}

function getCageLicenseCodes(licenses: { code: string }[]): Set<string> {
    return new Set(licenses.map((l) => l.code));
}

// ---------------------------------------------------------------------------
// Pure evaluator — reads declarative config, zero ID checks
// ---------------------------------------------------------------------------

/**
 * Evaluate a single AND rule group. Returns true if the item passes all rules.
 */
function evaluateRuleGroup(v: VisibilityRules, ctx: FilterContext): boolean {
    if (v.requiresNonSmolt && ctx.isSmolt) return false;
    // No code-level admin bypass for feature flags — admin access for flags
    // (e.g. feeding-dashboard) is managed via PostHog release conditions.
    if (v.requiredFeatureFlags?.length) {
        if (!v.requiredFeatureFlags.some((f) => ctx.isFeatureEnabled(f))) return false;
    }

    // requiresLicenses: admins bypass license checks entirely
    if (v.requiresLicenses?.length && !ctx.isOptoScaleAdmin) {
        if (!v.requiresLicenses.some((key) => ctx.activeLicenseKeys.has(key))) return false;
    }
    if (v.requiresWinch && !ctx.winchExists) return false;
    if (v.requiresBioscopeV1 && !ctx.isBioscopeV1) return false;
    if (v.requiresBioscopeV2 && !ctx.isBioscopeV2) return false;
    if (v.requiresNewBioscope && !ctx.isColorBioscope) return false;
    if (v.requiresLiceType === 'standard' && ctx.isChileanLice) return false;
    if (v.requiresLiceType === 'chilean' && !ctx.isChileanLice) return false;
    // hasNoLicenses: hidden when any of the listed licenses IS active.
    // Admins bypass this check (consistent with requiresLicenses bypass).
    if (v.hasNoLicenses?.length && !ctx.isOptoScaleAdmin) {
        if (v.hasNoLicenses.some((key) => ctx.activeLicenseKeys.has(key))) return false;
    }
    if (v.hasNoFeatureFlags?.length) {
        if (v.hasNoFeatureFlags.some((f) => ctx.isFeatureEnabled(f))) return false;
    }
    if (v.requiresClientAdmin && !ctx.isClientAdmin) return false;
    if (v.requiresCurrentLocationAdmin && !ctx.canAdministerCurrentLocation) return false;
    if (v.requiresOptoScaleAdmin && !ctx.isOptoScaleAdmin) return false;
    if (v.requiresAuditLogAccess && !ctx.canViewAuditLog) return false;

    return true;
}

/**
 * Check if an item passes all visibility rules.
 *
 * visibility can be:
 * - undefined: always visible
 * - single object (AND): all rules in the group must pass
 * - array of objects (OR): item is visible if ANY group passes
 */
function itemIsVisible(item: SidebarNavItem, ctx: FilterContext): boolean {
    const v = item.visibility;
    if (!v) return true;

    if (Array.isArray(v)) {
        return v.some((group) => evaluateRuleGroup(group, ctx));
    }

    return evaluateRuleGroup(v, ctx);
}

function filterItems(items: SidebarNavItem[], ctx: FilterContext): SidebarNavItem[] {
    return items
        .filter((item) => itemIsVisible(item, ctx))
        .map((item) => {
            if (!item.children?.length) return item;
            const filteredChildren = filterItems(item.children, ctx);
            return {
                ...item,
                children: filteredChildren.length > 0 ? filteredChildren : undefined,
            };
        });
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useSidebarFilter(
    level: SidebarLevel | null,
    params: Record<string, string>
): (items: SidebarNavItem[]) => SidebarNavItem[] {
    const clientId = params.clientId ? Number.parseInt(params.clientId, 10) : 0;
    const locationId = params.locationId ? Number.parseInt(params.locationId, 10) : 0;
    const cageId = params.cageId ? Number.parseInt(params.cageId, 10) : 0;
    const {
        isOptoScaleAdmin,
        canViewAuditLog,
        isClientAdmin,
        hasFullClientAccess: hasFullClientAccessPermission,
        canAdministerLocation = () => false,
    } = usePermissions();

    const locationLicenses = useGetLocationLicenses(clientId, {
        enabled: level === 'location' && clientId > 0,
    });

    // The pen's context, never a fish group's: the sidebar reads licences, cage type and
    // bioscope, all of which describe the pen.
    const { data: cageContextData } = useGetCageContext(
        cageId,
        {},
        { enabled: (level === 'cage' || level === 'wounds') && cageId > 0 }
    );

    const activeFlags = useActiveFeatureFlags();
    // A page held open by its flag gate keeps its nav item while the flag reads off.
    const heldOpen = useHeldOpenFlags();

    const isFeatureEnabled = useCallback(
        (key: string) => (activeFlags?.includes(key) ?? false) || isHeldOpen(heldOpen, key),
        [activeFlags, heldOpen]
    );
    const hasFullClientAccess = useCallback(
        (id: number) =>
            hasFullClientAccessPermission?.(id) ??
            (isOptoScaleAdmin || (id > 0 && isClientAdmin(id))),
        [hasFullClientAccessPermission, isOptoScaleAdmin, isClientAdmin]
    );

    const ctx = useMemo<FilterContext>(() => {
        const isAdmin = hasFullClientAccess(clientId);
        const currentLocationId = cageContextData?.location?.id ?? locationId;
        const canAdministerCurrentLocation = canAdministerLocation(clientId, currentLocationId);

        // Build active license keys based on current level
        let activeLicenseKeys = new Set<string>();
        if (level === 'location' && locationLicenses.data) {
            const locationData = locationLicenses.data.find((l) => l.locationId === locationId);
            if (locationData) {
                activeLicenseKeys = getActiveLicenseKeys(locationData.licenses);
            }
        } else if ((level === 'cage' || level === 'wounds') && cageContextData) {
            activeLicenseKeys = getCageLicenseCodes(cageContextData.licenses);
        }

        const isCageScopedLevel = level === 'cage' || level === 'wounds';

        // Non-cage-scoped levels: only licenses + permissions + feature flags apply
        if (!isCageScopedLevel || !cageContextData) {
            return {
                ...DEFAULT_CAGE_VALUES,
                isClientAdmin: isAdmin,
                canAdministerCurrentLocation,
                isOptoScaleAdmin,
                canViewAuditLog,
                isFeatureEnabled,
                activeLicenseKeys,
            };
        }
        return {
            isSmolt: isSmoltProductionTank(cageContextData.cage.cageType),
            winchExists: cageContextData.bioscope.winchExists,
            isBioscopeV1: cageContextData.bioscope.version === '1.0',
            isBioscopeV2: isBioscopeV2(cageContextData.bioscope.version),
            isColorBioscope: isColorBioscopeVersion(cageContextData.bioscope.version),
            isChileanLice: isChileanLiceType(cageContextData.stock.liceType),
            isClientAdmin: isAdmin,
            canAdministerCurrentLocation,
            isOptoScaleAdmin,
            canViewAuditLog,
            isFeatureEnabled,
            activeLicenseKeys,
        };
    }, [
        level,
        locationLicenses.data,
        locationId,
        cageContextData,
        isFeatureEnabled,
        isOptoScaleAdmin,
        canViewAuditLog,
        hasFullClientAccess,
        canAdministerLocation,
        clientId,
    ]);

    return useCallback((items: SidebarNavItem[]) => filterItems(items, ctx), [ctx]);
}
